import React, { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { useIntegrations } from "../hooks/useApi";
import { appById } from "../components/integrations/registry";
import { useSalvaIntegrazioni } from "../components/integrations/shared";
import { PILLOLA_SEZIONE } from "../components/integrations/casella";
import { AppTile, BrandStyles, MeshBackdrop, StatusPill, statoApp, VARIABILI_SEZIONE } from "../components/integrations/brand";

/**
 * Pagina di un'app di Apps & Integrazioni (/bots/:id/integrazioni/:app):
 * intestazione con l'interruttore, uguale per tutte, e sotto le impostazioni
 * specifiche dell'app (registry.js → Detail), con la sua anteprima.
 */
export default function IntegrationDetailPage() {
  const { id: botId, app: appId } = useParams();
  const app = appById(appId);
  // Le app in arrivo non hanno una pagina: si torna all'elenco.
  if (!app || app.inArrivo) return <Navigate to={`/bots/${botId}/integrazioni`} replace />;
  return <DettaglioApp key={`${botId}-${appId}`} botId={botId} app={app} />;
}

function DettaglioApp({ botId, app }) {
  const { data, isLoading } = useIntegrations(botId);
  const { salva, saving } = useSalvaIntegrazioni(botId);
  const settings = data?.[app.settingsKey];
  const Detail = app.Detail;
  const pronta = settings ? app.ready(settings) : false;
  // Cosa dice la pagina dell'app: si puo' accendere con quello che e' scritto
  // adesso, anche se non e' salvato? Allora accendere salva e attiva insieme.
  // Se la pagina lo dice, conta quello che si vede (la bozza), non quello che
  // e' salvato: togliere tutti i campi o il numero spegne l'interruttore.
  const [attivazione, setAttivazione] = useState(null);
  const accendibile = attivazione ? attivazione.pronta : pronta;

  const cambia = (enabled) => {
    if (enabled && attivazione?.pronta) return attivazione.attiva();
    return salva({ [app.settingsKey]: { enabled } }, `${app.title}: ${enabled ? "attiva" : "spenta"}`);
  };

  return (
    <div className="relative isolate" style={VARIABILI_SEZIONE}>
      <BrandStyles />

      {/* L'intestazione con lo sfondo dell'onboarding appena accennato, come
          nella pagina Widget. */}
      <div className="relative">
        <MeshBackdrop variante="onboarding" spazio={24} coda={260} />
        <header className="pu-fade-up mx-auto max-w-[1400px] space-y-6 pb-8 pt-2 sm:pt-4">
          <Link
            to={`/bots/${botId}/integrazioni`}
            className={`${PILLOLA_SEZIONE} transition-colors hover:bg-[rgba(173,70,255,0.18)]`}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Apps & Integrazioni
          </Link>

          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <AppTile app={app} size="lg" />
              <div className="min-w-0 space-y-1.5">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h1 className="text-[28px] font-semibold leading-9 tracking-tight text-[#020617]">{app.title}</h1>
                  {settings ? <StatusPill state={statoApp(app, settings)} /> : null}
                </div>
                <p className="max-w-xl text-base text-[#64748B]">{app.tagline}</p>
              </div>
            </div>

            {settings ? (
              <label className="flex w-full cursor-pointer items-center justify-between gap-6 rounded-[14px] border border-[#E2E8F0] bg-white px-4 py-3 lg:w-auto lg:min-w-[300px]">
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-[#020617]">
                    {settings.enabled ? "Attiva sull'assistente" : "Spenta"}
                  </span>
                  <span className="block text-xs leading-4 text-[#64748B]">
                    {settings.enabled
                      ? app.quandoAttiva || "L'assistente la usa quando serve."
                      : !accendibile
                        ? "Completa il passaggio 1 per accenderla."
                        : pronta
                          ? "Pronta: accendila quando vuoi."
                          : "Accendila: salvo anche quello che hai scritto."}
                  </span>
                </span>
                <Switch
                  checked={Boolean(settings.enabled)}
                  // Si spegne sempre; si accende solo quando e' pronta.
                  disabled={saving || (!settings.enabled && !accendibile)}
                  onCheckedChange={cambia}
                  aria-label={`${settings.enabled ? "Spegni" : "Attiva"} ${app.title}`}
                  className="data-[state=checked]:bg-[#19D163]"
                />
              </label>
            ) : null}
          </div>
        </header>
      </div>

      <div className="pu-fade-up mx-auto max-w-[1400px] pb-10">
        {isLoading || !settings ? (
          <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_420px] 2xl:grid-cols-[minmax(0,1fr)_520px]">
            <div className="space-y-4">
              <Skeleton className="h-40 w-full rounded-[14px]" />
              <Skeleton className="h-32 w-full rounded-[14px]" />
            </div>
            <Skeleton className="h-[680px] w-full rounded-[14px]" />
          </div>
        ) : (
          <Detail
            botId={botId}
            app={app}
            settings={settings}
            limits={data.limits}
            salva={salva}
            saving={saving}
            onAttivazione={setAttivazione}
          />
        )}
      </div>
    </div>
  );
}
